import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import createApp, { type ClientApplication } from "@shopify/app-bridge";

type AppBridgeContextValue = ClientApplication | null;

const AppBridgeContext = createContext<AppBridgeContextValue>(null);

export const useAppBridge = (): ClientApplication => {
  const value = useContext(AppBridgeContext);
  if (!value) {
    throw new Error("useAppBridge must be used within AppBridgeProvider");
  }
  return value;
};

export const useOptionalAppBridge = (): ClientApplication | null =>
  useContext(AppBridgeContext);

interface Props {
  children: React.ReactNode;
}

export const AppBridgeProvider: React.FC<Props> = ({ children }) => {
  const [hostError, setHostError] = useState<string | null>(null);

  const hostParam = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("host") : null;
  const isEmbeddedRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/embedded");

  useEffect(() => {
    if (!isEmbeddedRoute) {
      setHostError(null);
      return;
    }
    if (!hostParam) {
      console.error("[app-bridge] Missing host query param; cannot initialize App Bridge in embedded mode");
      setHostError("Missing Shopify host context. Please re-open the app from Shopify Admin.");
    } else {
      setHostError(null);
    }
  }, [hostParam, isEmbeddedRoute]);

  const app = useMemo(() => {
    if (typeof window === "undefined" || hostError) return null;
    const host = hostParam;
    if (!host) {
      return null;
    }

    const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY as string | undefined;
    if (!apiKey) {
      console.error("[app-bridge] Missing VITE_SHOPIFY_API_KEY env var");
      return null;
    }

    try {
      return createApp({ apiKey, host, forceRedirect: true });
    } catch (error) {
      console.error("[app-bridge] Failed to initialize", error);
      return null;
    }
  }, [hostParam, hostError]);

  if (hostError) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: "2rem" }}>
        <div style={{ maxWidth: 480, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12, padding: "2rem", textAlign: "center" }}>
          <h2 style={{ marginBottom: "0.75rem" }}>Open Cashflow AI from Shopify</h2>
          <p style={{ color: "#555" }}>{hostError}</p>
        </div>
      </div>
    );
  }

  return <AppBridgeContext.Provider value={app}>{children}</AppBridgeContext.Provider>;
};

export default AppBridgeProvider;
